import classNames from "classnames";
import { isEqual } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import usePrevious from "src/hooks/usePrevious";
import { ArrowDownIcon } from "src/icons";
import { LockIconV2 } from "src/icons/LockIconV2";
import { ACTIVE_APP } from "src/utils/constants";
import logo from "../../images/logo.png";
import {
  getActiveApp,
  getActiveTab,
  getUserRole,
  getUserStoresData,
} from "../../redux/selectors/app";
import useGetNav from "./useGetNav";

const Sidebar = () => {
  const activeTab = useSelector(getActiveTab);
  const activeApp = useSelector(getActiveApp);
  const userRole = useSelector(getUserRole);
  const userStores = useSelector(getUserStoresData);
  const hasStores = useMemo(() => userStores.length, [userStores.length]);

  const { navigationTabs } = useGetNav();
  const previousNavTabs = usePrevious(navigationTabs);
  const [menu, setMenu] = useState(expandOnInit(navigationTabs));

  useEffect(() => {
    if (!isEqual(navigationTabs, previousNavTabs)) {
      setMenu(navigationTabs);
    }
  }, [navigationTabs, previousNavTabs]);

  function isChildActive(children) {
    if (!children) return false;
    for (const element of children) {
      if (activeTab === element.name) {
        return true;
      }
    }
  }

  function expandOnInit(tabs) {
    return tabs.map((tab) =>
      isChildActive(tab.children) ? (tab.expanded = true) : tab
    );
  }

  const lockSidebar = useMemo(() => {
    if (activeApp === ACTIVE_APP.VENDOR) {
      return false;
    }
    return !hasStores && userRole !== 1;
  }, [activeApp, hasStores, userRole]);

  const handleExpandClick = (i) => {
    const newMenu = menu.map((m, index) => {
      if (i === index) {
        return (menu[index] = {
          ...menu[index],
          expanded: !menu[index].expanded,
        });
      }
      return m;
    });
    setMenu(newMenu);
  };

  return (
    <div className="flex grow flex-col overflow-y-auto bg-white pb-4 border-r w-[320px] scrollbar-thin">
      <div className="flex h-16 shrink-0 items-center justify-center mt-32px mb-44px">
        <img className="h-54px w-auto" src={logo} alt="Annex Crm Logo" />
      </div>
      <nav className="flex flex-1 flex-col mr-[22px]  h-full relative">
        <div
          className={classNames("h-full", {
            "blur-md": lockSidebar,
          })}
        >
          {menu?.map(
            ({ href, label, name, icon, expanded, children }, index) => (
              <Fragment key={`parent-tab-${name}`}>
                <SidebarItem
                  href={href}
                  handleExpandClick={handleExpandClick}
                  name={name}
                  icon={icon}
                  children={children}
                  label={label}
                  expanded={expanded}
                  index={index}
                  isActive={name === activeTab || isChildActive(children)}
                />

                {expanded &&
                  children?.map(({ href, label, name, icon }) => (
                    <SidebarItem
                      key={`child-tab-${name}`}
                      href={href}
                      label={label}
                      name={name}
                      icon={icon}
                      isActive={name === activeTab}
                      isChild
                    />
                  ))}
              </Fragment>
            )
          )}
        </div>
        {lockSidebar && (
          <div className="flex flex-col z-70 p-6 items-center text-center gap-10px absolute top-1/3">
            <LockIconV2 color={"var(--primary-color)"} />
            <div>The Feature will be unlocked once the store is setup.</div>
            <div className="flex items-center flex-nowrap w-full gap-3">
              <div className="min-h-3 bg-primary w-full rounded-md"></div>
              <span>100%</span>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;

const SidebarItem = ({
  href,
  handleExpandClick,
  icon: Icon,
  children,
  label,
  expanded,
  index,
  isActive,
  className,
  isChild,
}) => {
  const navigate = useNavigate();
  return (
    <div
      className={classNames(
        "flex w-full h-[50px] relative select-none",
        { "pl-14": isChild, "gap-7": !isChild },
        className
      )}
      onClick={() => (children ? handleExpandClick(index) : navigate(href))}
    >
      {!isChild && (
        <div
          className={classNames("min-w-2 rounded-r-md", {
            "!bg-primary": isActive,
            "bg-white": !isActive,
          })}
        />
      )}
      <div
        className={classNames(
          "group flex gap-x-3 rounded-md p-2 font-semibold cursor-pointer w-full items-center text-16px leading-6",
          {
            "bg-primary text-white": isActive && !isChild,
            "text-gray-700 hover:bg-gray-50": !isActive,
            "text-primary": isActive && isChild,
          }
        )}
      >
        {Icon !== undefined && (
          <Icon
            className={classNames("shrink-0", {
              "#FFFFFF": isActive && !isChild,
              "text-gray-400 group-hover:text-primary": !isActive,
            })}
            color={isActive ? (isChild ? "#0C8CE9" : "#FFFFFF") : undefined}
            aria-hidden="true"
          />
        )}

        {label}
      </div>

      {children && (
        <ArrowDownIcon
          className={classNames(
            "absolute right-2 top-0 bottom-0 my-auto transition-transform",
            {
              "rotate-180": expanded,
            }
          )}
          color={isActive ? "#FFFFFF" : undefined}
        />
      )}
    </div>
  );
};
